import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { taggingsKeys, tagsKeys, movesKeys, seriesKeys } from '@/api/queryKeys'
import {
  fetchTaggings,
  fetchTagging,
  fetchEntityTags,
  createTagging,
  updateTagging,
  deleteTagging,
  addTagsToEntity,
  removeTagFromEntity,
} from '@/api/taggings'
import type {
  CreateTaggingDto,
  UpdateTaggingDto,
  ApplyTagsToEntityDto,
  RemoveTagFromEntityDto,
} from '@/api/taggings'
import { useToast } from '@/hooks/use-toast'

/**
 * Hook to fetch all taggings with optional filters
 *
 * @param params - Query parameters for filtering
 * @returns React Query result with taggings list data
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useTaggings({ tagId: 'abc-123' });
 * ```
 */
export function useTaggings(params?: {
  tagId?: string
  taggableType?: 'move' | 'series'
  taggableId?: string
  includeRelations?: boolean
  limit?: number
  offset?: number
}) {
  return useQuery({
    queryKey: taggingsKeys.list(params),
    queryFn: () => fetchTaggings(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Hook to fetch a single tagging by ID
 *
 * @param id - Tagging ID
 * @returns React Query result with tagging details
 *
 * @example
 * ```tsx
 * const { data: tagging } = useTagging('tagging-id');
 * ```
 */
export function useTagging(id: string) {
  return useQuery({
    queryKey: taggingsKeys.detail(id),
    queryFn: () => fetchTagging(id),
    staleTime: 5 * 60 * 1000,
    enabled: !!id,
  })
}

/**
 * Hook to fetch all tags for a specific entity (move or series)
 *
 * @param entityType - Entity type ('move' or 'series')
 * @param entityId - Entity ID
 * @returns React Query result with entity tags
 *
 * @example
 * ```tsx
 * const { data: tags } = useEntityTags('move', 'move-id');
 * ```
 */
export function useEntityTags(entityType: 'move' | 'series', entityId: string) {
  return useQuery({
    queryKey: taggingsKeys.byEntity(entityType, entityId),
    queryFn: () => fetchEntityTags(entityType, entityId),
    staleTime: 5 * 60 * 1000,
    enabled: !!entityId && !!entityType,
  })
}

/**
 * Hook to create a new tagging relationship
 *
 * Automatically invalidates:
 * - Entity tags cache
 * - All taggings list
 * - Tag statistics
 *
 * @returns Mutation object with async create function
 *
 * @example
 * ```tsx
 * const createMutation = useCreateTagging();
 * await createMutation.mutateAsync({
 *   tagId: 'tag-id',
 *   taggableType: 'MOVE',
 *   taggableId: 'move-id'
 * });
 * ```
 */
export function useCreateTagging() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (data: CreateTaggingDto) => createTagging(data),
    onSuccess: newTagging => {
      // Invalidate entity tags
      queryClient.invalidateQueries({
        queryKey: taggingsKeys.byEntity(
          newTagging.taggableType,
          newTagging.taggableId
        ),
      })
      // Invalidate all taggings
      queryClient.invalidateQueries({
        queryKey: taggingsKeys.lists(),
      })
      // Invalidate tag statistics
      queryClient.invalidateQueries({
        queryKey: tagsKeys.statistics(newTagging.tagId),
      })
      // Invalidate entity detail (to update tag count)
      if (newTagging.taggableType === 'move') {
        queryClient.invalidateQueries({
          queryKey: movesKeys.detail(newTagging.taggableId),
        })
      } else if (newTagging.taggableType === 'series') {
        queryClient.invalidateQueries({
          queryKey: seriesKeys.detail(newTagging.taggableId),
        })
      }
      toast({
        title: 'Tag added',
        description: 'Tag has been added successfully.',
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to add tag',
        description: error.message || 'Please try again.',
        variant: 'destructive',
      })
    },
  })
}

/**
 * Hook to update an existing tagging
 *
 * @returns Mutation object with async update function
 *
 * @example
 * ```tsx
 * const updateMutation = useUpdateTagging();
 * await updateMutation.mutateAsync({
 *   id: 'tagging-id',
 *   data: { tagId: 'new-tag-id' }
 * });
 * ```
 */
export function useUpdateTagging() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTaggingDto }) =>
      updateTagging(id, data),
    onSuccess: updatedTagging => {
      // Invalidate specific tagging
      queryClient.invalidateQueries({
        queryKey: taggingsKeys.detail(updatedTagging.id),
      })
      // Invalidate entity tags
      queryClient.invalidateQueries({
        queryKey: taggingsKeys.byEntity(
          updatedTagging.taggableType,
          updatedTagging.taggableId
        ),
      })
      toast({
        title: 'Tagging updated',
        description: 'Tagging has been updated successfully.',
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to update tagging',
        description: error.message || 'Please try again.',
        variant: 'destructive',
      })
    },
  })
}

/**
 * Hook to delete a tagging by ID
 *
 * @returns Mutation object with async delete function
 *
 * @example
 * ```tsx
 * const deleteMutation = useDeleteTagging();
 * await deleteMutation.mutateAsync('tagging-id');
 * ```
 */
export function useDeleteTagging() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (id: string) => deleteTagging(id),
    onSuccess: () => {
      // Invalidate all taggings
      queryClient.invalidateQueries({
        queryKey: taggingsKeys.all,
      })
      toast({
        title: 'Tag removed',
        description: 'Tag has been removed successfully.',
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to remove tag',
        description: error.message || 'Please try again.',
        variant: 'destructive',
      })
    },
  })
}

/**
 * Hook to add multiple tags to an entity
 *
 * @returns Mutation object with async add function
 *
 * @example
 * ```tsx
 * const addTagsMutation = useAddTagsToEntity();
 * await addTagsMutation.mutateAsync({
 *   entityType: 'move',
 *   entityId: 'move-id',
 *   data: { tagIds: ['tag-1', 'tag-2'] }
 * });
 * ```
 */
export function useAddTagsToEntity() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({
      entityType,
      entityId,
      data,
    }: {
      entityType: 'move' | 'series'
      entityId: string
      data: ApplyTagsToEntityDto
    }) => addTagsToEntity(entityType, entityId, data),
    onSuccess: (_, variables) => {
      // Invalidate entity tags
      queryClient.invalidateQueries({
        queryKey: taggingsKeys.byEntity(
          variables.entityType,
          variables.entityId
        ),
      })
      // Invalidate all taggings
      queryClient.invalidateQueries({
        queryKey: taggingsKeys.lists(),
      })
      // Invalidate entity detail
      if (variables.entityType === 'move') {
        queryClient.invalidateQueries({
          queryKey: movesKeys.detail(variables.entityId),
        })
      } else {
        queryClient.invalidateQueries({
          queryKey: seriesKeys.detail(variables.entityId),
        })
      }
      // Toast will be shown by the calling component after refetch
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to add tags',
        description: error.message || 'Please try again.',
        variant: 'destructive',
      })
    },
  })
}

/**
 * Hook to remove a tag from an entity
 *
 * @returns Mutation object with async remove function
 *
 * @example
 * ```tsx
 * const removeTagMutation = useRemoveTagFromEntity();
 * await removeTagMutation.mutateAsync({
 *   entityType: 'move',
 *   entityId: 'move-id',
 *   data: { tagId: 'tag-id' }
 * });
 * ```
 */
export function useRemoveTagFromEntity() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({
      entityType,
      entityId,
      data,
    }: {
      entityType: 'move' | 'series'
      entityId: string
      data: RemoveTagFromEntityDto
    }) => removeTagFromEntity(entityType, entityId, data),
    onSuccess: (_, variables) => {
      // Invalidate entity tags
      queryClient.invalidateQueries({
        queryKey: taggingsKeys.byEntity(
          variables.entityType,
          variables.entityId
        ),
      })
      // Invalidate all taggings
      queryClient.invalidateQueries({
        queryKey: taggingsKeys.lists(),
      })
      // Invalidate entity detail
      if (variables.entityType === 'move') {
        queryClient.invalidateQueries({
          queryKey: movesKeys.detail(variables.entityId),
        })
      } else {
        queryClient.invalidateQueries({
          queryKey: seriesKeys.detail(variables.entityId),
        })
      }
      toast({
        title: 'Tag removed',
        description: 'Tag has been removed successfully.',
      })
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to remove tag',
        description: error.message || 'Please try again.',
        variant: 'destructive',
      })
    },
  })
}
